import { CountryCode, getCepAddress, initialAddressValues } from '@src/common/auth';
import { Translate } from '@src/i18n/useTranslate';
import { cpf, cnpj } from 'cpf-cnpj-validator';

import { z } from 'zod';
import { ForeignIdentityType, IdentityType } from './utils';
import { parse, format } from 'date-fns';

export const schema = (t: Translate['i18n'], isEdit: boolean) => {
  const requiredMessage = t('direct_link_required_field');

  const requiredStringField = z.string({ required_error: requiredMessage }).nonempty({ message: requiredMessage });
  const notRequiredStringField = z.string().optional();

  return z
    .object({
      firstName: requiredStringField,
      lastName: requiredStringField,
      email: requiredStringField,
      emailConfirmation: notRequiredStringField,
      phone: requiredStringField,
      identity: requiredStringField,
      identityType: z.enum([IdentityType.CPF, IdentityType.CNPJ], { required_error: requiredMessage }),
      corporateName: z.nullable(notRequiredStringField),
      countryCode: notRequiredStringField,
      iAmForeign: z.boolean(),
      foreignIdentityType: z.nullable(
        z.enum([ForeignIdentityType.PASSPORT, ForeignIdentityType.FOREIGN_ID]).optional(),
      ),
      zipCode: notRequiredStringField.nullable(),
      birthdate: notRequiredStringField.nullable(),
    })
    .superRefine(
      (
        {
          emailConfirmation,
          email,
          iAmForeign,
          identity,
          countryCode,
          identityType,
          corporateName,
          phone,
          birthdate,
          zipCode,
        },
        ctx,
      ) => {
        if (!isEdit && !emailConfirmation) {
          ctx.addIssue({
            code: 'custom',
            message: t('direct_link_required_field'),
            path: ['emailConfirmation'],
          });
        }

        if (!isEdit && email !== emailConfirmation) {
          ctx.addIssue({
            code: 'custom',
            message: t('customer_form_email_does_not_match'),
            path: ['emailConfirmation'],
          });
        }

        if (!phone || phone.length < 5) {
          ctx.addIssue({
            code: 'custom',
            message: t('direct_link_required_field'),
            path: ['phone'],
          });
        }

        if (
          !iAmForeign &&
          ((identityType === IdentityType.CPF && !cpf.isValid(identity)) ||
            (identityType === IdentityType.CNPJ && !cnpj.isValid(identity)))
        ) {
          ctx.addIssue({
            code: 'custom',
            message: t('customer_form_invalid_document'),
            path: ['identity'],
          });
        }

        if (iAmForeign && !countryCode) {
          ctx.addIssue({
            code: 'custom',
            message: t('direct_link_required_field'),
            path: ['countryCode'],
          });
        }

        if (identityType === IdentityType.CNPJ && !corporateName?.trim()) {
          ctx.addIssue({
            code: 'custom',
            message: t('direct_link_required_field'),
            path: ['corporateName'],
          });
        }

        if (birthdate) {
          const birthdateObj = new Date(birthdate);
          const today = new Date();

          if (birthdateObj >= today) {
            ctx.addIssue({
              code: 'custom',
              message: t('customer_form_invalid_birthdate'),
              path: ['birthdate'],
            });
          }
        }

        if (countryCode == CountryCode.BRAZIL && zipCode && zipCode.length < 8) {
          ctx.addIssue({
            code: 'custom',
            message: t('direct_link_zipCode_max_length_error'),
            path: ['zipCode'],
          });
        }
      },
    )
    .transform(async (data) => {
      const getAddress = async () => {
        const cleanedZipCode = data.zipCode?.replace(/[^0-9]/g, '') || null;
        if (!cleanedZipCode) return null;

        if (data.iAmForeign) {
          return {
            ...initialAddressValues,
            zipCode: cleanedZipCode,
          };
        }

        try {
          return await getCepAddress(cleanedZipCode);
        } catch {
          return {
            ...initialAddressValues,
            zipCode: cleanedZipCode,
          };
        }
      };

      const getBirthdate = () => {
        if (!data.birthdate) return null;

        if (data.birthdate.includes('/')) {
          return data.birthdate.split('/').reverse().join('-');
        }

        const parsedDate = parse(data.birthdate, 'dd/MM/yyyy', new Date());
        return format(parsedDate, 'yyyy-MM-dd');
      };

      return {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        corporateName: data.corporateName,
        email: data.email.trim(),
        emailConfirmation: data.email.trim(),
        phone: data.phone.replace(/[^0-9+]/g, ''),
        identity: data.identity.replace(/[^0-9a-zA-Z]/g, ''),
        countryCode: data.iAmForeign ? data.countryCode : CountryCode.BRAZIL,
        identityType: data.iAmForeign ? data.foreignIdentityType : data.identityType,
        birthdate: getBirthdate(),
        address: await getAddress(),
      };
    });
};
